import React, { useMemo, useState } from "react";
import InfoSheet from "@/components/InfoSheet";
import FacadeBadges from "@/components/FacadeBadges";
import { getElementInfo, zoneToInfoKind, GENERAL_PLAN_INFO } from "@/lib/element_info";

const F = Number.isFinite;

function zoneColour(kind, label) {
  const k = (kind || label || "").toLowerCase();
  if (/pool|مسبح/.test(k)) return "#0EA5E9";
  if (/garden|حديقة/.test(k)) return "#10B981";
  if (/terrace|balcony|شرفة|تراس|بلكون/.test(k)) return "#A78BFA";
  if (/courtyard|فناء/.test(k)) return "#F59E0B";
  if (/parking|موقف/.test(k)) return "#64748B";
  if (/shop|store|محل|دكان|متجر/.test(k)) return "#F59E0B";
  if (/corridor|hall|distributor|ممر|بهو|موزع|ردهة|توزيع/.test(k)) return "#CBD5E1";
  if (/bath|wc|kitchen|حمام|مطبخ/.test(k)) return "#67E8F9";
  return "#94A3B8";
}

// Classify a text-only label as a shaft/vent/skylight so we can draw a
// proper engineering symbol behind it (light well, air-shaft, skylight).
function shaftType(label) {
  const k = (label || "").toLowerCase();
  if (/skylight|فتحة سقف|منور سقف/.test(k)) return "skylight";
  if (/airshaft|air shaft|shaft|منور|فتحة تهوية|تهوية|vent/.test(k)) return "lightwell";
  return null;
}

/**
 * PlanSVG - AutoCAD-style architectural plan renderer.
 * Draws: property/plot line (setback strip = neighbours), walls with real
 * thickness, door swing arcs, double-glazed windows, columns, stairs with
 * treads + direction arrow, entry arrows, light-well / air-shaft / skylight
 * symbols, room labels, north arrow, scale bar and a legend.
 */
export default function PlanSVG({ model, w = 800, h = 600, showLabels = true, showScale = false, showPlot = true, showLegend = false, showDims = false, pad = 30, background = "#ffffff", overlay = null, circulation = null, showBlocks = false, furniture = null, roomAreas = null, showFurniture = false, explain = false, lang = "ar" }) {
  const ar = lang === "ar";
  const [sel, setSel] = useState(null);
  const openInfo = (kind, extra, hl) => {
    const info = getElementInfo(kind);
    if (info) setSel({ info, extra: extra || [], hl: hl || null });
  };
  const openPlan = () => setSel({ info: GENERAL_PLAN_INFO, extra: [], hl: null });
  const geo = useMemo(() => {
    const walls = (model?.walls || []).filter((wl) => wl?.p1 && wl?.p2 && F(wl.p1.x) && F(wl.p1.y) && F(wl.p2.x) && F(wl.p2.y));
    if (!walls.length) return null;
    let xmin = Infinity, xmax = -Infinity, ymin = Infinity, ymax = -Infinity;
    for (const wl of walls) {
      xmin = Math.min(xmin, wl.p1.x, wl.p2.x);
      xmax = Math.max(xmax, wl.p1.x, wl.p2.x);
      ymin = Math.min(ymin, wl.p1.y, wl.p2.y);
      ymax = Math.max(ymax, wl.p1.y, wl.p2.y);
    }
    // Include the plot extent so the property line + setback (neighbour
    // strip) are visible around the building.
    const ew = Number(model?.extent_w), eh = Number(model?.extent_h);
    const hasPlot = showPlot && F(ew) && F(eh) && ew > 0 && eh > 0;
    if (hasPlot) { xmin = Math.min(xmin, 0); ymin = Math.min(ymin, 0); xmax = Math.max(xmax, ew); ymax = Math.max(ymax, eh); }
    const W = xmax - xmin || 1;
    const H = ymax - ymin || 1;
    const P = showDims ? Math.max(pad, 66) : pad;
    const scale = Math.min((w - P * 2) / W, (h - P * 2) / H);
    const offx = (w - W * scale) / 2 - xmin * scale;
    const offy = (h - H * scale) / 2 - ymin * scale;
    // Flip Y so the front/facade (smallest model-y) renders at the BOTTOM of
    // the drawing (street-at-bottom convention requested by the user).
    const yspan = ymin + ymax;
    return { px: (x) => x * scale + offx, py: (y) => (yspan - y) * scale + offy, scale, walls, hasPlot, ew, eh };
  }, [model, w, h, pad, showPlot, showDims]);

  if (!geo) {
    return (
      <div className="flex h-full min-h-[240px] w-full items-center justify-center bg-white text-slate-400" style={{ background }}>—</div>
    );
  }

  const { px, py, scale, walls, hasPlot, ew, eh } = geo;
  const allLabels = (model.room_labels_pos || []).filter((z) => z && F(z.x) && F(z.y));
  const vents = allLabels.filter((z) => z.zone_kind === "vent" && F(z.w) && F(z.h));
  // ── For each vent (منور), determine which rooms it serves. A room is
  // "served" when its bbox is within 1.0 m of the vent perimeter - that's
  // the daylight/ventilation reach of a real open-to-sky light-well.
  const ROOM_KINDS = new Set([
    "bedroom", "master", "living", "salon", "hall", "kitchen", "bath",
    "wc", "dining", "majlis", "office", "maid", "family", "corridor",
    "laundry", "storage", "stairs",
  ]);
  const roomsForVents = allLabels.filter((z) => F(z.w) && F(z.h) && z.zone_kind !== "vent" && z.zone_kind !== "corridor");
  const ventServed = vents.map((v) => {
    const vx0 = v.x - v.w / 2, vx1 = v.x + v.w / 2;
    const vy0 = v.y - v.h / 2, vy1 = v.y + v.h / 2;
    const dists = [];
    roomsForVents.forEach((r) => {
      const rx0 = r.x - r.w / 2, rx1 = r.x + r.w / 2;
      const ry0 = r.y - r.h / 2, ry1 = r.y + r.h / 2;
      const dx = Math.max(0, Math.max(vx0 - rx1, rx0 - vx1));
      const dy = Math.max(0, Math.max(vy0 - ry1, ry0 - vy1));
      const gap = Math.max(dx, dy);
      if (gap <= 3.0) dists.push({ gap, label: r.label || r.zone_kind || "" });
    });
    dists.sort((a, b) => a.gap - b.gap);
    return { vent: v, served: dists.slice(0, 4).map((d) => d.label) };
  });

  const corridors = allLabels.filter((z) => z.zone_kind === "corridor" && F(z.w) && F(z.h));
  const zones = allLabels.filter((z) => F(z.w) && F(z.h) && z.zone_kind !== "vent" && z.zone_kind !== "corridor");
  const shafts = allLabels.filter((z) => !(F(z.w) && F(z.h)) && shaftType(z.label));
  const openings = model.openings || [];
  const columns = (model.columns || []).filter((c) => c && c.centre && F(c.centre.x) && F(c.centre.y) && F(c.width) && F(c.depth));
  const stairs = (model.stairs || []).filter((s) => s && s.origin && F(s.origin.x) && F(s.origin.y) && F(s.width) && F(s.depth));
  const entries = (model.entry_markers || []).filter((m) => m && F(m.x) && F(m.y));

  // ── openings geometry (CAD symbols) ──
  const openEls = [];
  const openHits = [];
  openings.forEach((o, i) => {
    const wl = walls.find((x) => x.id === o.wall_id);
    if (!wl) return;
    const dx = wl.p2.x - wl.p1.x, dy = wl.p2.y - wl.p1.y;
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L, uy = dy / L, nx = -uy, ny = ux;
    const off = Number(o.offset_from_p1) || 0;
    const width = Number(o.width) || 0.9;
    const hw = width / 2;
    const cx = wl.p1.x + ux * off, cy = wl.p1.y + uy * off;
    if (F(cx) && F(cy)) openHits.push({ cx, cy, kind: o.kind, width, label: o.label });
    const ax = cx - ux * hw, ay = cy - uy * hw, bx = cx + ux * hw, by = cy + uy * hw;
    if (![ax, ay, bx, by].every(F)) return;
    const th = Number(wl.thickness) || 0.2;
    const cut = Math.max(3, th * scale);
    openEls.push(<line key={`cut-${i}`} x1={px(ax)} y1={py(ay)} x2={px(bx)} y2={py(by)} stroke="#ffffff" strokeWidth={cut + 1.5} strokeLinecap="butt" />);
    if (o.kind === "door") {
      const sign = o.swing === "out" ? -1 : 1;
      const hingeAtP2 = o.leaf_side === "p2";
      const hx = hingeAtP2 ? bx : ax, hy = hingeAtP2 ? by : ay;
      const jx = hingeAtP2 ? ax : bx, jy = hingeAtP2 ? ay : by;
      const tx = hx + nx * sign * width, ty = hy + ny * sign * width;
      const r = width * scale;
      openEls.push(<line key={`leaf-${i}`} x1={px(hx)} y1={py(hy)} x2={px(tx)} y2={py(ty)} stroke="#B45309" strokeWidth={1.6} />);
      openEls.push(<path key={`arc-${i}`} d={`M ${px(tx)} ${py(ty)} A ${r} ${r} 0 0 ${sign > 0 ? 1 : 0} ${px(jx)} ${py(jy)}`} fill="none" stroke="#B45309" strokeWidth={1.1} strokeDasharray="3 2" />);
    } else {
      const q = th / 4;
      openEls.push(<line key={`w1-${i}`} x1={px(ax + nx * q)} y1={py(ay + ny * q)} x2={px(bx + nx * q)} y2={py(by + ny * q)} stroke="#0EA5E9" strokeWidth={1.3} />);
      openEls.push(<line key={`w2-${i}`} x1={px(ax - nx * q)} y1={py(ay - ny * q)} x2={px(bx - nx * q)} y2={py(by - ny * q)} stroke="#0EA5E9" strokeWidth={1.3} />);
    }
  });

  // ── stairs (treads + direction arrow) ──
  const stairEls = [];
  stairs.forEach((s, i) => {
    const ox = s.origin.x, oy = s.origin.y;
    const sw = Number(s.width), sd = Number(s.depth);
    const steps = Math.max(2, Math.min(Number(s.steps) || 12, 40));
    const vertical = (s.orientation || "vertical") === "vertical";
    stairEls.push(<rect key={`st-${i}`} x={px(ox)} y={py(oy)} width={sw * scale} height={sd * scale} fill="#F1F5F9" stroke="#475569" strokeWidth={1.4} />);
    for (let k = 1; k < steps; k++) {
      if (vertical) {
        const yy = oy + (sd * k) / steps;
        stairEls.push(<line key={`st-${i}-t${k}`} x1={px(ox)} y1={py(yy)} x2={px(ox + sw)} y2={py(yy)} stroke="#94A3B8" strokeWidth={0.8} />);
      } else {
        const xx = ox + (sw * k) / steps;
        stairEls.push(<line key={`st-${i}-t${k}`} x1={px(xx)} y1={py(oy)} x2={px(xx)} y2={py(oy + sd)} stroke="#94A3B8" strokeWidth={0.8} />);
      }
    }
    // direction arrow along the flight centreline
    const up = (s.direction || "up") === "up";
    const cxm = ox + sw / 2, cym = oy + sd / 2;
    let x1, y1, x2, y2;
    if (vertical) { x1 = cxm; x2 = cxm; y1 = up ? oy + sd - 0.15 : oy + 0.15; y2 = up ? oy + 0.15 : oy + sd - 0.15; }
    else { y1 = cym; y2 = cym; x1 = up ? ox + sw - 0.15 : ox + 0.15; x2 = up ? ox + 0.15 : ox + sw - 0.15; }
    stairEls.push(<line key={`st-${i}-arr`} x1={px(x1)} y1={py(y1)} x2={px(x2)} y2={py(y2)} stroke="#0F172A" strokeWidth={1.8} markerEnd="url(#arrowStair)" />);
    stairEls.push(<text key={`st-${i}-lbl`} x={px(cxm)} y={py(cym)} fontSize={9} fontWeight="800" fill="#0F172A" textAnchor="middle" dominantBaseline="middle">{up ? "UP ↑" : "DN ↓"}</text>);
    // mid-landing (استراحة) for any flight taller than 8 risers - code requirement
    if (steps > 8) {
      const lx = vertical ? ox : ox + sw * 0.42;
      const ly = vertical ? oy + sd * 0.42 : oy;
      const lw = vertical ? sw : sw * 0.16;
      const lh = vertical ? sd * 0.16 : sd;
      stairEls.push(<rect key={`st-${i}-land`} x={px(lx)} y={py(ly)} width={lw * scale} height={lh * scale} fill="#E2E8F0" stroke="#475569" strokeWidth={1} />);
      stairEls.push(<text key={`st-${i}-landlbl`} x={px(lx + lw / 2)} y={py(ly + lh / 2)} fontSize={7} fontWeight="700" fill="#475569" textAnchor="middle" dominantBaseline="middle">استراحة</text>);
    }
  });

  const scaleBarM = 5;

  // ── dimension chains (auto-computed from geometry → accurate & auto-update on edit) ──
  const dimEls = [];
  if (showDims) {
    const r2 = (v) => (Math.round(v * 100) / 100).toFixed(2);
    let bx0 = Infinity, bx1 = -Infinity, by0 = Infinity, by1 = -Infinity;
    walls.forEach((wl) => { bx0 = Math.min(bx0, wl.p1.x, wl.p2.x); bx1 = Math.max(bx1, wl.p1.x, wl.p2.x); by0 = Math.min(by0, wl.p1.y, wl.p2.y); by1 = Math.max(by1, wl.p1.y, wl.p2.y); });
    const uniq = (arr) => [...new Set(arr.map((v) => Math.round(v * 20) / 20))].sort((a, b) => a - b);
    const vx = uniq(walls.filter((wl) => Math.abs(wl.p1.x - wl.p2.x) < 0.06).map((wl) => (wl.p1.x + wl.p2.x) / 2)).filter((x) => x > bx0 + 0.1 && x < bx1 - 0.1);
    const hy = uniq(walls.filter((wl) => Math.abs(wl.p1.y - wl.p2.y) < 0.06).map((wl) => (wl.p1.y + wl.p2.y) / 2)).filter((y) => y > by0 + 0.1 && y < by1 - 0.1);
    // Merge stops closer than a min segment (interior wall thickness ~0.10 m
    // otherwise prints redundant sub-segments that clutter the chain).
    const mergeClose = (arr, minSeg = 0.2) => {
      const s = [...arr].sort((a, b) => a - b);
      const out = [s[0]];
      for (let i = 1; i < s.length; i++) { if (s[i] - out[out.length - 1] >= minSeg) out.push(s[i]); }
      if (out[out.length - 1] !== s[s.length - 1]) out[out.length - 1] = s[s.length - 1];
      return out;
    };
    const xs = mergeClose([bx0, ...vx, bx1]), ys = mergeClose([by0, ...hy, by1]);
    const DIM = "#1E3A5F";
    const dimY = py(by1) + 24, totY = py(by1) + 42;
    xs.forEach((x, k) => dimEls.push(<line key={`ex${k}`} x1={px(x)} y1={py(by1) + 3} x2={px(x)} y2={totY} stroke={DIM} strokeWidth={0.6} strokeOpacity={0.45} />));
    for (let i = 0; i < xs.length - 1; i++) {
      const x1 = px(xs[i]), x2 = px(xs[i + 1]), seg = xs[i + 1] - xs[i];
      if (seg <= 0) continue;
      dimEls.push(<line key={`db${i}`} x1={x1} y1={dimY} x2={x2} y2={dimY} stroke={DIM} strokeWidth={1} />);
      dimEls.push(<line key={`dbt1${i}`} x1={x1} y1={dimY - 3} x2={x1} y2={dimY + 3} stroke={DIM} strokeWidth={1} />);
      dimEls.push(<line key={`dbt2${i}`} x1={x2} y1={dimY - 3} x2={x2} y2={dimY + 3} stroke={DIM} strokeWidth={1} />);
      dimEls.push(<text key={`dbx${i}`} x={(x1 + x2) / 2} y={dimY - 4} fontSize={9} fontWeight="700" fill={DIM} textAnchor="middle">{r2(seg)}</text>);
    }
    dimEls.push(<line key="tot-b" x1={px(bx0)} y1={totY} x2={px(bx1)} y2={totY} stroke={DIM} strokeWidth={1.3} />);
    dimEls.push(<text key="tot-bx" x={(px(bx0) + px(bx1)) / 2} y={totY + 13} fontSize={10} fontWeight="800" fill={DIM} textAnchor="middle">{r2(bx1 - bx0)} m</text>);
    const dimX = px(bx0) - 24, totX = px(bx0) - 42;
    ys.forEach((y, k) => dimEls.push(<line key={`ey${k}`} x1={px(bx0) - 3} y1={py(y)} x2={totX} y2={py(y)} stroke={DIM} strokeWidth={0.6} strokeOpacity={0.45} />));
    for (let i = 0; i < ys.length - 1; i++) {
      const y1 = py(ys[i]), y2 = py(ys[i + 1]), seg = ys[i + 1] - ys[i];
      if (seg <= 0) continue;
      dimEls.push(<line key={`dl${i}`} x1={dimX} y1={y1} x2={dimX} y2={y2} stroke={DIM} strokeWidth={1} />);
      dimEls.push(<line key={`dlt1${i}`} x1={dimX - 3} y1={y1} x2={dimX + 3} y2={y1} stroke={DIM} strokeWidth={1} />);
      dimEls.push(<line key={`dlt2${i}`} x1={dimX - 3} y1={y2} x2={dimX + 3} y2={y2} stroke={DIM} strokeWidth={1} />);
      const my = (y1 + y2) / 2;
      dimEls.push(<text key={`dly${i}`} x={dimX - 4} y={my} fontSize={9} fontWeight="700" fill={DIM} textAnchor="middle" transform={`rotate(-90 ${dimX - 4} ${my})`}>{r2(seg)}</text>);
    }
    dimEls.push(<line key="tot-l" x1={totX} y1={py(by0)} x2={totX} y2={py(by1)} stroke={DIM} strokeWidth={1.3} />);
    const myAll = (py(by0) + py(by1)) / 2;
    dimEls.push(<text key="tot-ly" x={totX - 10} y={myAll} fontSize={10} fontWeight="800" fill={DIM} textAnchor="middle" transform={`rotate(-90 ${totX - 10} ${myAll})`}>{r2(by1 - by0)} m</text>);
  }

  // ── explain mode: transparent hit-test layer + selection highlight ──
  const hitEls = [];
  let hlEl = null;
  if (explain) {
    const num = (v) => Math.round(v * 100) / 100;
    const areaChip = (a) => ({ label: ar ? "المساحة" : "Area", value: `${num(a)} م²` });
    const push = (key, x, y, ww, hh, kind, extra) =>
      hitEls.push(<rect key={key} x={x} y={y} width={Math.max(8, ww)} height={Math.max(8, hh)} fill="transparent" style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); openInfo(kind, extra, { x, y, w: Math.max(8, ww), h: Math.max(8, hh) }); }} />);
    // background → general plan info (rendered first = below the specific hits)
    hitEls.push(<rect key="hit-plan" x={0} y={0} width={w} height={h} fill="transparent" style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); openPlan(); }} />);
    walls.forEach((wl, i) => {
      const len = Math.hypot(wl.p2.x - wl.p1.x, wl.p2.y - wl.p1.y);
      const isExt = (wl.wall_type || "").toLowerCase() === "exterior";
      hitEls.push(<line key={`hw${i}`} x1={px(wl.p1.x)} y1={py(wl.p1.y)} x2={px(wl.p2.x)} y2={py(wl.p2.y)} stroke="transparent" strokeWidth={14} style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); openInfo(isExt ? "wall_exterior" : "wall_interior", [{ label: ar ? "الطول" : "Length", value: `${num(len)} م` }, { label: ar ? "السمك" : "Thick", value: `${Math.round((Number(wl.thickness) || 0.15) * 100)} سم` }], null); }} />);
    });
    zones.forEach((z, i) => push(`hz${i}`, px(z.x - z.w / 2), py(z.y - z.h / 2), z.w * scale, z.h * scale, zoneToInfoKind(z.zone_kind, z.label), [areaChip(z.w * z.h)]));
    corridors.forEach((z, i) => push(`hc${i}`, px(z.x - z.w / 2), py(z.y - z.h / 2), z.w * scale, z.h * scale, "corridor", [areaChip(z.w * z.h)]));
    allLabels.filter((z) => z.zone_kind !== "vent" && z.zone_kind !== "corridor" && !(F(z.w) && F(z.h))).forEach((z, i) => {
      const s = px(z.x), t = py(z.y);
      push(`hr${i}`, s - 28, t - 13, 56, 26, zoneToInfoKind(z.zone_kind, z.label), z.label ? [{ label: ar ? "الاسم" : "Name", value: String(z.label) }] : []);
    });
    vents.forEach((v, i) => push(`hv${i}`, px(v.x - v.w / 2), py(v.y + v.h / 2), v.w * scale, v.h * scale, "light_well", [areaChip(v.w * v.h), { label: ar ? "المقاس" : "Size", value: `${num(v.w)}×${num(v.h)} م` }]));
    stairs.forEach((s, i) => push(`hst${i}`, px(s.origin.x), py(s.origin.y), Number(s.width) * scale, Number(s.depth) * scale, "stair", []));
    columns.forEach((c, i) => push(`hcol${i}`, px(c.centre.x) - (c.width * scale) / 2, py(c.centre.y) - (c.depth * scale) / 2, c.width * scale, c.depth * scale, "column", [{ label: ar ? "المقاس" : "Size", value: `${Math.round(c.width * 100)}×${Math.round(c.depth * 100)} سم` }]));
    openHits.forEach((o, i) => { const s = px(o.cx), t = py(o.cy); push(`ho${i}`, s - 9, t - 9, 18, 18, o.kind, [{ label: ar ? "العرض" : "Width", value: `${num(o.width)} م` }]); });
    entries.forEach((m, i) => { const s = px(m.x), t = py(m.y); push(`he${i}`, s - 13, t - 13, 26, 26, "entry", []); });
  }
  if (sel && sel.hl) {
    const { x, y, w: hw2, h: hh2 } = sel.hl;
    hlEl = <rect x={x - 2} y={y - 2} width={hw2 + 4} height={hh2 + 4} fill="#22D3EE" fillOpacity={0.14} stroke="#0891B2" strokeWidth={2.4} strokeDasharray="6 3" pointerEvents="none" />;
  }

  const svg = (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ background, maxWidth: "100%" }} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <marker id="arrowStair" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="#0F172A" />
        </marker>
        <marker id="arrowEntry" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="#DC2626" />
        </marker>
        <pattern id="hatchWell" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="#0EA5E9" strokeWidth="1" />
        </pattern>
        <pattern id="hatchPlot" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="8" stroke="#CBD5E1" strokeWidth="1" />
        </pattern>
        <pattern id="hatchCorr" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="7" height="7" fill="#F1F5F9" />
          <line x1="0" y1="0" x2="0" y2="7" stroke="#94A3B8" strokeWidth="0.8" />
        </pattern>
      </defs>

      {/* plot / property line + neighbour setback strip (hatched) */}
      {hasPlot && (
        <g>
          <rect x={px(0)} y={py(0)} width={ew * scale} height={eh * scale} fill="url(#hatchPlot)" fillOpacity={0.5} stroke="#DC2626" strokeWidth={1.4} strokeDasharray="8 4" />
          <text x={px(ew / 2)} y={py(0) - 6} fontSize={10} fontWeight="700" fill="#DC2626" textAnchor="middle">Property line · حد الأرض (الجيران)</text>
        </g>
      )}

      {/* Facade context badges - extracted into a shared component so the
          same SVG snippet + rules-overlay behaviour is reused across web
          PlanSVG, Foundation, and the mobile surfaces. */}
      {hasPlot && showLabels && (
        <FacadeBadges
          meta={model?.facade_meta || {}}
          designCode={model?.design_code || model?.structural_meta?.design_code}
          positions={{
            front: { cx: px(ew / 2), cy: py(0) + 20 },
            rear:  { cx: px(ew / 2), cy: py(eh) - 22 },
            left:  { cx: px(0) - 6,  cy: py(eh / 2) },
            right: { cx: px(ew) + 6, cy: py(eh / 2) },
          }}
        />
      )}

      {/* zones (terrace/balcony/pool/garden/courtyard) */}
      {zones.map((z, i) => (
        <rect key={`z-${i}`} x={px(z.x - z.w / 2)} y={py(z.y - z.h / 2)} width={z.w * scale} height={z.h * scale} fill={zoneColour(z.zone_kind, z.label)} opacity={0.28} stroke={zoneColour(z.zone_kind, z.label)} strokeWidth={1} strokeOpacity={0.6} />
      ))}

      {/* circulation corridors / distributors - hatched fill so the movement
          path from the entrance to every room reads clearly (AutoCAD style) */}
      {corridors.map((z, i) => (
        <g key={`corr-${i}`}>
          <rect x={px(z.x - z.w / 2)} y={py(z.y - z.h / 2)} width={z.w * scale} height={z.h * scale} fill="url(#hatchCorr)" stroke="#64748B" strokeWidth={1.2} strokeDasharray="5 3" />
        </g>
      ))}

      {/* walls with real thickness */}
      {walls.map((wl, i) => {
        const isExt = (wl.wall_type || "").toLowerCase() === "exterior";
        const th = Number(wl.thickness) || (isExt ? 0.25 : 0.15);
        return (
          <line key={`w-${i}`} x1={px(wl.p1.x)} y1={py(wl.p1.y)} x2={px(wl.p2.x)} y2={py(wl.p2.y)} stroke={isExt ? "#0F172A" : "#334155"} strokeWidth={Math.max(2, th * scale)} strokeLinecap="butt" />
        );
      })}

      {/* opening symbols */}
      {openEls}

      {/* stairs */}
      {stairEls}

      {/* ventilation light-wells - shared shaft (منور) with windows on each
          side for daylight + cross-ventilation; area shown for economy.
          A small badge tags the served rooms so users see at a glance
          which spaces the shaft ventilates. */}
      {ventServed.map(({ vent: v, served }, i) => {
        const wPx = v.w * scale, hPx = v.h * scale;
        const x = px(v.x - v.w / 2), y = py(v.y + v.h / 2);
        const roomsShort = (served || [])
          .map((s) => String(s).split(/[·\-—]/)[0].trim().split(/\s+/)[0])
          .filter(Boolean)
          .slice(0, 3);
        const badgeText = roomsShort.length
          ? `${roomsShort.join(" · ")}${served.length > 3 ? ` +${served.length - 3}` : ""}`
          : "";
        return (
          <g key={`vent-${i}`}>
            <rect x={x} y={y} width={wPx} height={hPx} fill="url(#hatchWell)" stroke="#0EA5E9" strokeWidth={1.3} />
            {/* windows on the four faces (each surrounding room opens onto it) */}
            <line x1={x + wPx * 0.3} y1={y} x2={x + wPx * 0.7} y2={y} stroke="#2563EB" strokeWidth={2.2} />
            <line x1={x + wPx * 0.3} y1={y + hPx} x2={x + wPx * 0.7} y2={y + hPx} stroke="#2563EB" strokeWidth={2.2} />
            <line x1={x} y1={y + hPx * 0.3} x2={x} y2={y + hPx * 0.7} stroke="#2563EB" strokeWidth={2.2} />
            <line x1={x + wPx} y1={y + hPx * 0.3} x2={x + wPx} y2={y + hPx * 0.7} stroke="#2563EB" strokeWidth={2.2} />
            {showLabels && (
              <>
                <text x={px(v.x)} y={py(v.y) + 3} fontSize={7.5} fontWeight="800" textAnchor="middle" fill="#0369A1" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 2.5 }}>منور {(v.w * v.h).toFixed(1)}م²</text>
                {badgeText && (
                  <g data-testid={`vent-badge-${i}`}>
                    <rect x={px(v.x) - Math.max(24, badgeText.length * 3.6)} y={y + hPx + 3}
                          width={Math.max(48, badgeText.length * 7.2)} height={12}
                          rx={5} ry={5}
                          fill="#0EA5E9" fillOpacity={0.95} stroke="#0369A1" strokeWidth={0.6} />
                    <text x={px(v.x)} y={y + hPx + 12}
                          fontSize={7} fontWeight="800" textAnchor="middle" fill="#F8FAFC"
                          style={{ paintOrder: "stroke", stroke: "#0369A1", strokeWidth: 0.6 }}>
                      يخدم: {badgeText}
                    </text>
                  </g>
                )}
              </>
            )}
            <title>{`منور تهوية وإنارة ${v.w.toFixed(1)}×${v.h.toFixed(1)} م - يخدم ${served.length} ${served.length === 1 ? "غرفة" : "غرف"}${served.length ? ": " + served.join("، ") : ""}`}</title>
          </g>
        );
      })}

      {/* light-well / air-shaft / skylight symbols */}
      {shafts.map((s, i) => {
        const type = shaftType(s.label);
        const size = 1.1 * scale;
        const cx = px(s.x), cy = py(s.y) - size * 0.55;
        if (type === "skylight") {
          return (
            <g key={`sh-${i}`}>
              <rect x={cx - size / 2} y={cy - size / 2} width={size} height={size} fill="#FEF9C3" stroke="#CA8A04" strokeWidth={1.2} />
              <line x1={cx - size / 2} y1={cy - size / 2} x2={cx + size / 2} y2={cy + size / 2} stroke="#CA8A04" strokeWidth={0.9} />
              <line x1={cx + size / 2} y1={cy - size / 2} x2={cx - size / 2} y2={cy + size / 2} stroke="#CA8A04" strokeWidth={0.9} />
            </g>
          );
        }
        return (
          <g key={`sh-${i}`}>
            <rect x={cx - size / 2} y={cy - size / 2} width={size} height={size} fill="url(#hatchWell)" stroke="#0EA5E9" strokeWidth={1.2} />
          </g>
        );
      })}

      {/* columns */}
      {columns.map((c, i) => (
        <rect key={`c-${i}`} x={px(c.centre.x) - (c.width * scale) / 2} y={py(c.centre.y) - (c.depth * scale) / 2} width={Math.max(4, c.width * scale)} height={Math.max(4, c.depth * scale)} transform={c.angle ? `rotate(${c.angle} ${px(c.centre.x)} ${py(c.centre.y)})` : undefined} fill="#1E293B" stroke="#0EA5E9" strokeWidth={1} />
      ))}

      {/* symbolic furniture blocks (bed / sofa / kitchen / bath) - make the
          plan read like a real furnished professional drawing */}
      {showFurniture && (furniture || []).map((f, i) => {
        if (![f.x, f.y, f.w, f.h].every(F)) return null;
        const rx = px(f.x), ry = py(f.y + f.h), fw = f.w * scale, fh = f.h * scale;
        const stroke = "#475569";
        const fills = { bed: "#E0F2FE", wardrobe: "#F1F5F9", sofa: "#DCFCE7", table: "#FEF9C3", counter: "#F1F5F9", toilet: "#E2E8F0", sink: "#E2E8F0" };
        const fill = fills[f.type] || "#F1F5F9";
        const els = [<rect key="r" x={rx} y={ry} width={fw} height={fh} rx={f.type === "sofa" || f.type === "toilet" ? 4 : 1.5} fill={fill} stroke={stroke} strokeWidth={1} />];
        if (f.type === "bed") els.push(<line key="p" x1={rx} y1={ry + fh * 0.28} x2={rx + fw} y2={ry + fh * 0.28} stroke={stroke} strokeWidth={0.9} />);
        if (f.type === "sofa") { els.push(<line key="b" x1={rx} y1={ry + fh * 0.55} x2={rx + fw} y2={ry + fh * 0.55} stroke={stroke} strokeWidth={0.9} />); }
        if (f.type === "counter") els.push(<rect key="s" x={rx + fw * 0.6} y={ry + fh * 0.2} width={Math.max(4, fw * 0.25)} height={Math.max(4, fh * 0.55)} fill="#CBD5E1" stroke={stroke} strokeWidth={0.7} />);
        if (f.type === "wardrobe") els.push(<line key="d" x1={rx} y1={ry} x2={rx + fw} y2={ry + fh} stroke={stroke} strokeWidth={0.6} />);
        return <g key={`furn-${i}`} pointerEvents="none">{els}</g>;
      })}

      {/* per-room net-area tags (m²) for at-a-glance efficiency reading */}
      {showLabels && (roomAreas || []).map((r, i) => (F(r.x) && F(r.y) && F(r.area)) ? (
        <text key={`area-${i}`} x={px(r.x)} y={py(r.y) + 13} fontSize={9} fontWeight="700" fill="#0369A1" textAnchor="middle" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 2.5 }}>{r.area} م²</text>
      ) : null)}

      {/* entry arrows */}
      {entries.map((m, i) => {
        const len = 1.4;
        const dx = Number(m.dx) || 0, dy = Number(m.dy) || 1;
        const dl = Math.hypot(dx, dy) || 1;
        const x1 = m.x - (dx / dl) * len, y1 = m.y - (dy / dl) * len;
        return (
          <g key={`en-${i}`}>
            <line x1={px(x1)} y1={py(y1)} x2={px(m.x)} y2={py(m.y)} stroke="#DC2626" strokeWidth={2.4} markerEnd="url(#arrowEntry)" />
            <text x={px(x1)} y={py(y1) - 4} fontSize={10} fontWeight="800" fill="#DC2626" textAnchor="middle">ENTRY · مدخل</text>
          </g>
        );
      })}

      {/* labels */}
      {showLabels && allLabels.filter((z) => z.zone_kind !== "vent").map((z, i) => (
        <text key={`t-${i}`} x={px(z.x)} y={py(z.y)} fontSize={11} fontWeight="600" fill="#0F172A" textAnchor="middle" dominantBaseline="middle">{z.label}</text>
      ))}

      {/* wall BLOCK sizing tags - exterior vs interior block thickness (cm) */}
      {showBlocks && walls.filter((wl) => Math.hypot(wl.p2.x - wl.p1.x, wl.p2.y - wl.p1.y) > 1.8).map((wl, i) => {
        const mx = (wl.p1.x + wl.p2.x) / 2, my = (wl.p1.y + wl.p2.y) / 2;
        let ang = Math.atan2(wl.p2.y - wl.p1.y, wl.p2.x - wl.p1.x) * 180 / Math.PI;
        if (Math.abs(ang) > 90) ang += 180;
        const ext = wl.wall_type === "exterior" || (Number(wl.thickness) || 0) >= 0.18;
        const cm = Math.round((Number(wl.thickness) || 0.15) * 100);
        const col = ext ? "#DC2626" : "#0EA5E9";
        return (
          <text key={`blk-${i}`} x={px(mx)} y={py(my)} fontSize={7.5} fontWeight="800" fill={col} textAnchor="middle" transform={`rotate(${ang} ${px(mx)} ${py(my)})`} dy={-2.5}>
            {`${cm}cm ${ext ? "EXT·خارجي" : "INT·داخلي"}`}
          </text>
        );
      })}

      {/* FACADE identification - front facade at the entry, from real entry markers */}
      {showBlocks && entries.length > 0 && (() => {
        const e = entries[0];
        const dx = Number(e.dx) || 0, dy = Number(e.dy) || 1;
        const dl = Math.hypot(dx, dy) || 1;
        const fx = e.x + (dx / dl) * 1.6, fy = e.y + (dy / dl) * 1.6;
        return (
          <g key="facade-front">
            <rect x={px(fx) - 58} y={py(fy) - 9} width={116} height={16} rx={4} fill="#DC2626" opacity={0.92} />
            <text x={px(fx)} y={py(fy) + 3} fontSize={9} fontWeight="900" fill="#ffffff" textAnchor="middle">الواجهة الأمامية · FRONT</text>
          </g>
        );
      })()}

      {/* dimension chains (auto-updating) */}
      {dimEls}

      {/* scale bar + north arrow */}
      {showScale && (
        <g>
          <line x1={pad} y1={h - 14} x2={pad + scaleBarM * scale} y2={h - 14} stroke="#0F172A" strokeWidth={2.5} />
          <line x1={pad} y1={h - 18} x2={pad} y2={h - 10} stroke="#0F172A" strokeWidth={2.5} />
          <line x1={pad + scaleBarM * scale} y1={h - 18} x2={pad + scaleBarM * scale} y2={h - 10} stroke="#0F172A" strokeWidth={2.5} />
          <text x={pad + (scaleBarM * scale) / 2} y={h - 20} fontSize={10} fontWeight="700" fill="#0F172A" textAnchor="middle">{scaleBarM} m</text>
          <g transform={`translate(${w - pad - 14}, ${pad + 4})`}>
            <path d="M0,-14 L6,10 L0,4 L-6,10 Z" fill="#0F172A" />
            <text x="0" y="24" fontSize="10" fontWeight="800" fill="#0F172A" textAnchor="middle">N</text>
          </g>
        </g>
      )}

      {/* legend */}
      {showLegend && (
        <g transform={`translate(${pad}, ${pad})`} fontSize={9} fontWeight="700" fill="#0F172A">
          <rect x={-6} y={-14} width={168} height={120} rx={6} fill="#ffffff" stroke="#CBD5E1" strokeWidth={1} opacity={0.96} />
          <rect x={0} y={-8} width={12} height={8} fill="url(#hatchPlot)" stroke="#DC2626" strokeWidth={1} strokeDasharray="3 2" /><text x={18} y={-1}>حد الأرض / الجيران</text>
          <rect x={0} y={6} width={12} height={8} fill="#A78BFA" opacity={0.4} /><text x={18} y={13}>تراس / بلكونة</text>
          <rect x={0} y={20} width={12} height={8} fill="url(#hatchWell)" stroke="#0EA5E9" strokeWidth={1} /><text x={18} y={27}>منور / تهوية (بشبابيك)</text>
          <rect x={0} y={34} width={12} height={8} fill="url(#hatchCorr)" stroke="#64748B" strokeWidth={1} strokeDasharray="3 2" /><text x={18} y={41}>ممر توزيع (موزع)</text>
          <rect x={0} y={48} width={12} height={8} fill="#FEF9C3" stroke="#CA8A04" strokeWidth={1} /><text x={18} y={55}>فتحة سقف (Skylight)</text>
          <rect x={2} y={62} width={9} height={8} fill="#1E293B" stroke="#0EA5E9" strokeWidth={1} /><text x={18} y={69}>عمود خرساني (Column)</text>
          <line x1={0} y1={80} x2={12} y2={80} stroke="#B45309" strokeWidth={3} /><line x1={14} y1={80} x2={24} y2={80} stroke="#0EA5E9" strokeWidth={2.4} /><text x={30} y={83}>باب / شباك</text>
          <line x1={0} y1={94} x2={12} y2={94} stroke="#DC2626" strokeWidth={2.2} markerEnd="url(#arrowEntry)" /><text x={18} y={97}>مدخل / أدراج</text>
        </g>
      )}
      {/* overlay networks (electrical / plumbing / drainage networks) */}
      {overlay && (
        <g>
          {(overlay.lines || []).map((ln, i) => {
            const pts = (ln.pts || []).filter((p) => F(p.x) && F(p.y));
            if (pts.length < 2) return null;
            const d = pts.map((p, j) => `${j ? "L" : "M"} ${px(p.x)} ${py(p.y)}`).join(" ");
            return <path key={`ov-l-${i}`} d={d} fill="none" stroke={ln.color || "#0EA5E9"} strokeWidth={ln.width || 1.8} strokeDasharray={ln.dash || undefined} strokeLinejoin="round" strokeLinecap="round" opacity={0.95} />;
          })}
          {(overlay.points || []).map((p, i) => (F(p.x) && F(p.y)) ? (
            <g key={`ov-p-${i}`}>
              <circle cx={px(p.x)} cy={py(p.y)} r={p.r || 4} fill={p.color || "#F59E0B"} stroke="#0F172A" strokeWidth={0.8} />
              {p.label && <text x={px(p.x)} y={py(p.y) - 6} fontSize={8} fontWeight="800" fill={p.color || "#0F172A"} textAnchor="middle">{p.label}</text>}
            </g>
          ) : null)}
        </g>
      )}
      {/* Circulation map - entrance → corridors/halls → every room */}
      {circulation && (
        <g>
          <defs>
            <marker id="circArrow" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 1 L 8 5 L 0 9 z" fill="#16A34A" />
            </marker>
            <marker id="circArrowBad" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 1 L 8 5 L 0 9 z" fill="#DC2626" />
            </marker>
          </defs>
          {(circulation.paths || []).map((p, i) => {
            const pts = (p.points || []).filter((q) => Array.isArray(q) && F(q[0]) && F(q[1]));
            if (pts.length < 2) return null;
            const d = pts.map((q, j) => `${j ? "L" : "M"} ${px(q[0])} ${py(q[1])}`).join(" ");
            const col = p.reachable ? "#16A34A" : "#DC2626";
            return (
              <path key={`circ-${i}`} d={d} fill="none" stroke={col} strokeWidth={2.2}
                strokeDasharray={p.reachable ? undefined : "6 4"} strokeLinejoin="round" strokeLinecap="round"
                opacity={0.9} markerEnd={`url(#${p.reachable ? "circArrow" : "circArrowBad"})`} />
            );
          })}
          {circulation.entry && F(circulation.entry[0]) && F(circulation.entry[1]) && (
            <circle cx={px(circulation.entry[0])} cy={py(circulation.entry[1])} r={5} fill="#16A34A" stroke="#fff" strokeWidth={1.5} />
          )}
        </g>
      )}
      {/* North arrow (top-right) + graphic scale bar (bottom-right).
          Facade is at the bottom (south), so North points UP. */}
      <g>
        <g transform={`translate(${w - pad - 16}, ${pad + 22})`}>
          <line x1={0} y1={14} x2={0} y2={-14} stroke="#0F172A" strokeWidth={2} markerEnd="url(#arrowStair)" />
          <text x={0} y={-18} fontSize={11} fontWeight="900" fill="#0F172A" textAnchor="middle">N</text>
        </g>
        {(() => {
          const barM = scale >= 22 ? 2 : scale >= 12 ? 5 : 10;
          const barPx = barM * scale;
          const bx = w - pad - barPx;
          const by = h - pad - 4;
          return (
            <g>
              <rect x={bx - 4} y={by - 16} width={barPx + 8} height={22} rx={4} fill="#ffffff" stroke="#CBD5E1" strokeWidth={1} opacity={0.95} />
              <line x1={bx} y1={by} x2={bx + barPx} y2={by} stroke="#0F172A" strokeWidth={2} />
              <line x1={bx} y1={by - 4} x2={bx} y2={by} stroke="#0F172A" strokeWidth={2} />
              <line x1={bx + barPx} y1={by - 4} x2={bx + barPx} y2={by} stroke="#0F172A" strokeWidth={2} />
              <line x1={bx + barPx / 2} y1={by - 3} x2={bx + barPx / 2} y2={by} stroke="#0F172A" strokeWidth={1} />
              <text x={bx + barPx / 2} y={by - 6} fontSize={8.5} fontWeight="800" fill="#0F172A" textAnchor="middle">{barM} m</text>
            </g>
          );
        })()}
      </g>
      {/* selection highlight (explain mode) */}
      {hlEl}
      {/* transparent hit-test layer (explain mode) - on top so clicks register */}
      {explain && hitEls}
    </svg>
  );
  if (!explain) return svg;
  return (
    <>
      {svg}
      {sel && <InfoSheet info={sel.info} extra={sel.extra} ar={ar} onClose={() => setSel(null)} />}
    </>
  );
}
